import {EditorView} from "@buildwithflux/constants";
import {EnterpriseUid, OrganizationUid} from "@buildwithflux/identifiers-schema/legacy";
import {z} from "zod";

/**
 * These routes are used in App.tsx, the main application routing, but are also used for reverse routing (aka generating
 * dynamic URLs) in the application code.
 *
 * Because both useParams() and reverse routing depend on the exact name of the captured parameters, make sure you search
 * the codebase before renaming a parameter (such as `:handle`)
 *
 * Note that some things use this collection as a whole, to try to match against pathname (e.g. <Markdown />). This
 * doesn't work for routes containing any placeholders, but is nonetheless important to be aware of before removing
 * anything from this object.
 */
export const Routes = {
    CREATE_ORGANIZATION: "/organization/create",
    SIGN_UP: "/signup",
    LOG_IN: "/login",
    LOG_OUT: "/logout",
    // NOTE: for reset password routes, also see config in
    // https://console.firebase.google.com/project/graviton-mvp/authentication/emails
    RESET_PASSWORD: "/reset-password",
    RESET_PASSWORD_CALLBACK: "/reset-password/__/auth/action",
    VERIFY_EMAIL: "/verify-email",
    LANDING: "/",
    CMS_ROOT: "/p",
    GET_STARTED: "/p/get-started",
    COPILOT: "/copilot",
    BLOG: "/blog",

    ME: "/me",
    MY: "/my",
    ACCOUNT: "/account",
    SEARCH: "/search",
    SITEMAP: "/sitemap",
    SITEMAP_CATEGORY: "/sitemap/:category",
    SITEMAP_SUBCATEGORY: "/sitemap/:category/:subcategory",

    CREATE_DOCUMENT: "/projects/create",
    CREATE_DOCUMENT_FROM_TEMPLATE: "/projects/createDocumentFromTemplate",
    FORK_DOCUMENT: "/projects/forkProject",
    CLONE_DOCUMENT: "/projects/cloneProject",
    CLONE_EXAMPLE_DOCUMENT: "/projects/cloneExampleProject",
    CLONE_LEARNING_DOCUMENT: "/projects/cloneLearningProject",
    RESTORE_DOCUMENT_TO_NEW: "/projects/restoreProjectToNew",

    /**
     * We use this url to handle any external redirection
     */
    REDIRECT: "/redirect",
    MCP_OAUTH_CALLBACK: "/oauth/mcp/callback",
    MCP_OAUTH_CONSENT: "/oauth/mcp-consent",
    INTERNAL_DYNAMIC: "/internal/:section?/:subsection?",

    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    PROJECT_BY_UID: "/projects/:documentUid",
    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    PROJECT_BY_SLUG: "/:handle/:documentSlug",
    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    LOCAL_COMPONENT_BY_SLUG: "/:handle/:baseSlug/:componentSlug", // Try to avoid using this many wildcards in a row in future

    // NOTE: this route is handled by cloudflare in production. It doesn't work in localhost.
    STORYBOOK: "/storybook",

    /** @deprecated use LOG_IN except in BC code */
    SIGN_IN: "/signin",
    /** @deprecated use LOG_OUT except in BC code */
    SIGN_OUT: "/signout",
} as const;

export enum Origin {
    Menu = "Menu",
    Hotkey = "Hotkey",
    Profile = "Profile",
    NavBar = "NavBar",
    AutoLayoutAnnouncement = "AutoLayoutAnnouncement",
    BigChatAnnouncement = "BigChatAnnouncement",
}

type OriginMatrix = {
    Clone: [Origin.Menu, Origin.Hotkey, Origin.Profile, Origin.AutoLayoutAnnouncement, Origin.BigChatAnnouncement];
    New: [Origin.Menu, Origin.Hotkey, Origin.Profile, Origin.BigChatAnnouncement];
    Fork: [Origin.Menu, Origin.NavBar];
};

type ExtractOrigins<K extends keyof OriginMatrix> = OriginMatrix[K][number];

export type CloneProjectOrigin = ExtractOrigins<"Clone">;
export type NewProjectOrigin = ExtractOrigins<"New">;
export type ForkProjectOrigin = ExtractOrigins<"Fork">;

export function createNewProjectRoute(
    origin: NewProjectOrigin,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("origin", origin);

    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }

    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }

    return `${Routes.CREATE_DOCUMENT}?${searchParams.toString()}`;
}

export function cloneDocumentRoute(
    documentUid: string,
    origin: CloneProjectOrigin,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);

    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }

    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }

    return `${Routes.CLONE_DOCUMENT}?${searchParams.toString()}`;
}

export function cloneExampleProjectRoute(
    documentUid: string,
    origin: CloneProjectOrigin,
    editorView?: EditorView,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (editorView) {
        searchParams.set("editorMode", editorView);
    }
    return `${Routes.CLONE_EXAMPLE_DOCUMENT}?${searchParams.toString()}`;
}

export function cloneLearningFluxProjectRoute(
    documentUid: string,
    origin: CloneProjectOrigin,
    editorView?: EditorView,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (editorView) {
        searchParams.set("editorMode", editorView);
    }
    return `${Routes.CLONE_LEARNING_DOCUMENT}?${searchParams.toString()}`;
}

export function cloneDocumentFromTemplateRoute(
    documentUid: string,
    origin: CloneProjectOrigin | undefined,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);

    if (origin) {
        searchParams.set("origin", origin);
    }

    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }

    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }

    return `${Routes.CREATE_DOCUMENT_FROM_TEMPLATE}?${searchParams.toString()}`;
}

export function forkDocumentRoute(
    documentUid: string,
    origin: ForkProjectOrigin,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);

    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }

    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }

    return `${Routes.FORK_DOCUMENT}?${searchParams.toString()}`;
}

export function restoreDocumentToNewRoute(documentUid: string, actionRecordUid: string): string {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("actionRecordUid", actionRecordUid);

    return `${Routes.RESTORE_DOCUMENT_TO_NEW}?${searchParams.toString()}`;
}

const RedirectPageSearchParamsForDataSheet = z.object({
    type: z.literal("datasheet"),
    projectUid: z.string(),
});
type RedirectPageSearchParamsForDataSheet = z.infer<typeof RedirectPageSearchParamsForDataSheet>;

const httpUrl = z.string().regex(/^https?:\/\//, {message: "Only http/https URLs are allowed"});

const RedirectPageSearchParamsForCachedUrl = z.object({
    type: z.literal("cached_url"),
    originalUrl: httpUrl,
    cachedUrl: httpUrl,
});
type RedirectPageSearchParamsForCachedUrl = z.infer<typeof RedirectPageSearchParamsForCachedUrl>;

/**
 * Search parameters for the redirect page.
 *
 * - `type: "datasheet"` — legacy route that calls the backend to resolve URLs
 *   for a project's datasheet_url property. Kept for backward compatibility.
 * - `type: "cached_url"` — generic route for any property with a cached CDN
 *   copy. Both original and cached URLs are passed directly as search params.
 */
export const RedirectPageSearchParams = z.discriminatedUnion("type", [
    RedirectPageSearchParamsForDataSheet,
    RedirectPageSearchParamsForCachedUrl,
]);
export type RedirectPageSearchParams = z.infer<typeof RedirectPageSearchParams>;
export function externalRedirectRoute(params: RedirectPageSearchParams) {
    const searchParams = new URLSearchParams();

    searchParams.set("type", params.type);

    if (params.type === "datasheet") {
        searchParams.set("projectUid", params.projectUid);
    } else if (params.type === "cached_url") {
        searchParams.set("originalUrl", params.originalUrl);
        searchParams.set("cachedUrl", params.cachedUrl);
    }

    return `${Routes.REDIRECT}?${searchParams.toString()}`;
}
